import { useEffect } from 'react';
import { api, ensureCsrf } from '../lib/api';
import { disconnectEcho } from '../lib/echo';
import { useAuthStore } from '../stores/authStore';
import type { User } from '../types/api';

/**
 * Bootstrap: GET /sanctum/csrf-cookie + GET /api/v1/auth/me.
 * 401 sets user=null; any other state hydrates the auth store.
 *
 * Also refetches /auth/me on a 60s interval and on window focus so
 * server-side state changes (admin KYC approval, admin enabling
 * live_trading, email verification completing in another tab) reach
 * the portal without the user having to log out.
 */
export function useAuthBootstrap() {
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);

  useEffect(() => {
    let cancelled = false;

    const fetchMe = async (showLoading: boolean) => {
      if (showLoading) setLoading(true);
      try {
        const r = await api.get<User>('/auth/me');
        if (!cancelled) setUser(r.data);
      } catch {
        if (!cancelled && showLoading) setUser(null);
        // Background poll failure: keep the existing user; transient network
        // hiccups shouldn't kick the user back to login.
      } finally {
        if (!cancelled && showLoading) setLoading(false);
      }
    };

    (async () => {
      try {
        await ensureCsrf();
      } catch {
        // Non-fatal: fetchMe below will 401 and route to /login if the
        // session really is gone. Blocking boot on a CSRF hiccup is worse.
      }
      await fetchMe(true);
    })();

    const interval = window.setInterval(() => fetchMe(false), 60_000);
    const onFocus = () => fetchMe(false);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [setUser, setLoading]);
}

export async function login(email: string, password: string, remember = false) {
  await ensureCsrf();
  const r = await api.post<User>('/auth/login', { email, password, remember });
  useAuthStore.getState().setUser(r.data);
  return r.data;
}

export async function register(name: string, email: string, password: string, phone?: string) {
  await ensureCsrf();
  const r = await api.post<User>('/auth/register', {
    name, email, password, password_confirmation: password, phone,
  });
  useAuthStore.getState().setUser(r.data);
  return r.data;
}

/**
 * Force an immediate /auth/me refresh into the store. Used when a real-time
 * event (e.g. auto-trading paused on a daily-loss breach) changes server-side
 * user state we want reflected now instead of waiting for the 60s poll.
 */
export async function refreshUser() {
  try {
    const r = await api.get<User>('/auth/me');
    useAuthStore.getState().setUser(r.data);
  } catch {
    // Transient failure: the 60s background poll will reconcile.
  }
}

export async function logout() {
  try {
    await api.post('/auth/logout');
  } finally {
    useAuthStore.getState().clear();
    disconnectEcho();
  }
}

export function useUser(): User | null {
  return useAuthStore((s) => s.user);
}
